import type { CourseApiResponse, LessonApiResponse, QuestionApiResponse, SectionApiResponse } from "@tmi/types/api/content-models";
import type { Course, Product } from "@tmi/types/entities";
import { isFreelyAvailableContent } from "@/utils/access-utils";
import { useProductsStore } from "@/stores/Products";
import pinia from "@/plugins/PiniaPlugin";
import { usePermissionsStore } from "@/stores/Permissions";

export function mapCourseApiResponseToCourseCatalog( courses: CourseApiResponse[] ): Course[] {
    const permissionsStore = usePermissionsStore( pinia );
    const productsStore = useProductsStore( pinia );

    const loadedCourses = [ ...( courses
            .map( ( c: CourseApiResponse ): Course => ( {
                id: c.id,
                slug: c.slug,
                title: c.title,
                body: c.body,
                thumbnail_uri: c.thumbnail_uri,
                assoc_product_ids: c.assoc_product_ids,
                published_dt: c.published_dt,
                sections: !c.sections ? [] : c.sections.map( ( s: SectionApiResponse ) => ( {
                    id: s.id,
                    title: s.title,
                    sequence_number: s.sequence_number,
                    // Live Contentful data: a section without lessons or a question without answers must not throw
                    // and blank the whole catalog (tmi-ck2.44).
                    lessons: ( s.lessons ?? [] ).map( ( l: LessonApiResponse ) => ( {
                        id: l.id,
                        title: l.title,
                        slug: l.slug,
                        sequence_number: l.sequence_number,
                        vimeo_video_id: l.vimeo_video_id,
                        questions: !l.questions ? null : l.questions?.map( ( q: QuestionApiResponse ) => ( {
                            id: q.id,
                            question: q.question,
                            correct_answer_number: q.correct_answer_number,
                            answers: [ ...( q.answers ?? [] ) ],
                            clarification: q.clarification
                        } ) )
                    } ) )
                } ) )
            } ) )
    ) ];

    return loadedCourses.filter( ( c: Course ) => {
        return c.assoc_product_ids?.some( ( id: string ) => {
            const product: Product | undefined = productsStore.productById( id );
            return product && !!( product.codes ) &&
                ( permissionsStore.hasAccessBasedOnShortCode( product.codes ) || isFreelyAvailableContent( product.slug ) );
        } ) ?? false;
    } );
}